import { Check, ChevronDown, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useMaterialCategories } from '@/hooks/useProcedureVolumeReport'
import type { ReportDefinition, ReportFilterDimension } from '@/lib/reportCatalog'
import {
  clearDimension,
  toggleFilter,
  type ReportFilterSelection,
} from '@/lib/reportFilters'
import {
  REPORT_GRAINS,
  REPORT_PRESETS,
  presetWindow,
  windowLabel,
  windowLengthDays,
  type ReportGrain,
  type ReportTimeFrame,
} from '@/lib/reportRange'

/**
 * The report's controls, in one bar above its content.
 *
 * Dimensions on the left, period on the right, and they never overlap: the left
 * asks what a row IS, the right asks WHEN. Each dimension is a dropdown rather
 * than a permanent list, so a report with four of them costs one line, and what
 * is actually applied reads back as chips underneath.
 *
 * Everything applies on change. The legacy screen made you press Run because
 * each pane was a round trip; here the view is the URL, so a click IS the
 * change — and `replace` keeps a dozen experiments out of the back button.
 */
export function ReportToolbar({
  report,
  frame,
  filters,
  onFrame,
  onFilters,
}: {
  report: ReportDefinition
  frame: ReportTimeFrame
  filters: ReportFilterSelection
  onFrame: (next: ReportTimeFrame) => void
  onFilters: (next: ReportFilterSelection) => void
}) {
  const active = report.filters.filter((d) => (filters[d.id]?.length ?? 0) > 0)

  return (
    <div className='flex flex-col gap-2 border-b px-4 py-2.5'>
      <div className='flex flex-wrap items-center gap-2'>
        {report.filters.map((dimension) => (
          <DimensionMenu
            key={dimension.id}
            dimension={dimension}
            selected={filters[dimension.id] ?? []}
            onToggle={(optionId) => onFilters(toggleFilter(filters, dimension.id, optionId))}
            onClear={() => onFilters(clearDimension(filters, dimension.id))}
          />
        ))}

        <div className='ml-auto flex flex-wrap items-center gap-2'>
          {report.hasGrain && (
            <Segmented
              options={REPORT_GRAINS}
              value={frame.grain}
              onChange={(grain) => onFrame({ ...frame, grain: grain as ReportGrain })}
            />
          )}
          <PeriodControl frame={frame} onChange={onFrame} />
        </div>
      </div>

      {active.length > 0 && (
        <div className='flex flex-wrap items-center gap-1.5'>
          {active.flatMap((dimension) =>
            (filters[dimension.id] ?? []).map((optionId) => (
              <button
                key={`${dimension.id}:${optionId}`}
                type='button'
                onClick={() => onFilters(toggleFilter(filters, dimension.id, optionId))}
                className='inline-flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20'
              >
                {labelOf(dimension, optionId)}
                <X className='h-3 w-3' aria-label={`Remove ${labelOf(dimension, optionId)}`} />
              </button>
            )),
          )}
          <button
            type='button'
            onClick={() => onFilters({})}
            className='px-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground'
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  )
}

function DimensionMenu({
  dimension,
  selected,
  onToggle,
  onClear,
}: {
  dimension: ReportFilterDimension
  selected: string[]
  onToggle: (optionId: string) => void
  onClear: () => void
}) {
  // A row-backed dimension names where its options come from; everything else
  // carries them in the catalog. Fetched here rather than lifted out of the
  // rendered report, so the filter is usable before the first request.
  const dynamic = useDimensionOptions(dimension)
  const options = dimension.options ?? dynamic.options
  // Pending only while there is genuinely nothing to show. A source that
  // resolved to an empty list is an answer — the tenant has no categories —
  // and the menu says so rather than staying disabled forever.
  const pending = options === null

  return (
    <Popover>
      <PopoverTrigger
        disabled={pending}
        title={pending ? 'Options load with the report' : undefined}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors',
          pending && 'cursor-not-allowed opacity-50',
          !pending && selected.length > 0
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        {dimension.label}
        {selected.length > 0 && (
          <span className='rounded-full bg-primary/20 px-1.5 tabular-nums'>{selected.length}</span>
        )}
        <ChevronDown className='h-3 w-3 opacity-60' />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-56 p-1'>
        {options?.length === 0 && (
          <p className='px-2 py-1.5 text-xs text-muted-foreground'>Nothing to filter by.</p>
        )}
        {options?.map((option) => {
          const on = selected.includes(option.id)
          return (
            <button
              key={option.id}
              type='button'
              role='checkbox'
              aria-checked={on}
              onClick={() => onToggle(option.id)}
              className='flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted'
            >
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                  on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                )}
              >
                {on && <Check className='h-3 w-3' />}
              </span>
              <span className='truncate'>{option.label}</span>
            </button>
          )
        })}
        {selected.length > 0 && (
          <button
            type='button'
            onClick={onClear}
            className='mt-1 w-full border-t px-2 py-1.5 text-left text-xs text-muted-foreground hover:text-foreground'
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}

function PeriodControl({
  frame,
  onChange,
}: {
  frame: ReportTimeFrame
  onChange: (next: ReportTimeFrame) => void
}) {
  const custom = frame.preset === 'custom'

  return (
    <div className='flex items-center rounded-lg border p-0.5'>
      {REPORT_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type='button'
          aria-pressed={frame.preset === preset.id}
          onClick={() => onChange({ ...frame, preset: preset.id, window: presetWindow(preset.id, new Date()) })}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            frame.preset === preset.id
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {preset.label.replace('Last ', '')}
        </button>
      ))}

      <Popover>
        <PopoverTrigger
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            custom
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {custom ? windowLabel(frame.window) : 'Custom'}
          <ChevronDown className='h-3 w-3 opacity-60' />
        </PopoverTrigger>
        <PopoverContent align='end' className='flex w-56 flex-col gap-2 p-3'>
          <label className='text-xs font-medium text-muted-foreground' htmlFor='report-from'>
            Start date
          </label>
          <input
            id='report-from'
            type='date'
            aria-label='Start date'
            value={frame.window.from}
            max={frame.window.to}
            onChange={(e) =>
              e.target.value &&
              onChange({
                ...frame,
                preset: 'custom',
                window: { from: e.target.value, to: frame.window.to },
              })
            }
            className='h-8 rounded-md border bg-background px-2 text-sm'
          />
          <label className='text-xs font-medium text-muted-foreground' htmlFor='report-to'>
            End date
          </label>
          <input
            id='report-to'
            type='date'
            aria-label='End date'
            value={frame.window.to}
            min={frame.window.from}
            onChange={(e) =>
              e.target.value &&
              onChange({
                ...frame,
                preset: 'custom',
                window: { from: frame.window.from, to: e.target.value },
              })
            }
            className='h-8 rounded-md border bg-background px-2 text-sm'
          />
          <p className='text-xs text-muted-foreground'>
            {windowLengthDays(frame.window)} days, both ends included
          </p>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { id: string; label: string }[]
  value: string
  onChange: (id: string) => void
}) {
  return (
    <div className='flex items-center rounded-lg border p-0.5'>
      {options.map((option) => (
        <button
          key={option.id}
          type='button'
          aria-pressed={value === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            value === option.id
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function labelOf(dimension: ReportFilterDimension, optionId: string): string {
  // A row-backed option's id IS its label — the value is the category name —
  // so falling through to the id is correct rather than a fallback.
  return dimension.options?.find((o) => o.id === optionId)?.label ?? optionId
}

/**
 * The options for a dimension whose values live in the tenant's data.
 *
 * `null` means "not known yet"; an empty array means "known, and there are
 * none". The two render differently, which is why this does not collapse them.
 */
function useDimensionOptions(dimension: ReportFilterDimension): {
  options: { id: string; label: string }[] | null
} {
  const categories = useMaterialCategories()
  if (dimension.optionsSource !== 'materialCategory') return { options: null }
  if (!categories.data) return { options: null }
  return { options: categories.data.categories.map((id) => ({ id, label: id })) }
}
